import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/unsubscribe-email`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Unsubscribe — Bravax Forms";
  }, []);

  const unsubscribe = async () => {
    setState("loading");
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON,
          Authorization: `Bearer ${ANON}`,
        },
        body: JSON.stringify({ email, token }),
      });
      const json = await res.json();
      if (json.ok) setState("done");
      else {
        setError(json.error || "Failed");
        setState("error");
      }
    } catch (e: any) {
      setError(e?.message || "Network error");
      setState("error");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle>Unsubscribe from emails</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!email || !token ? (
            <p className="text-sm text-muted-foreground">
              Invalid unsubscribe link.
            </p>
          ) : state === "done" ? (
            <div className="flex flex-col items-center text-center gap-3 py-4">
              <CheckCircle2 className="h-10 w-10 text-emerald-500" />
              <p className="text-sm">
                <strong>{email}</strong> has been unsubscribed from Bravax
                Forms broadcast emails.
              </p>
            </div>
          ) : state === "error" ? (
            <div className="flex flex-col items-center text-center gap-3 py-4">
              <AlertCircle className="h-10 w-10 text-destructive" />
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Click the button below to unsubscribe{" "}
                <strong>{email}</strong> from Bravax Forms broadcast emails.
                You'll still receive transactional emails (receipts, password
                resets, form submission notifications).
              </p>
              <Button
                className="w-full"
                disabled={state === "loading"}
                onClick={unsubscribe}
              >
                {state === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Confirm unsubscribe
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default Unsubscribe;
